import React, { useState, useEffect } from 'react';
import pb from '../../../lib/pocketbase';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import './AccountDashboard.css'; // Re-use styles

const Quotes = ({ user }) => {
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewQuote, setPreviewQuote] = useState(null);
    const [companySettings, setCompanySettings] = useState(null);

    useEffect(() => {
        if (!user?.id) return;

        const loadData = async () => {
            try {
                // Fetch Quotes
                const records = await pb.collection('quotes').getFullList({
                    filter: `users = "${user.id}"`,
                    sort: '-created',
                });
                setQuotes(records);

                // Fetch Company Settings
                try {
                    const settings = await pb.collection('company_settings').getList(1, 1);
                    if (settings.items.length > 0) {
                        setCompanySettings(settings.items[0]);
                    }
                } catch (e) {
                    console.log("No custom settings found, using defaults");
                }

            } catch (err) {
                console.error('Erreur data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user?.id]);

    const generatePDF = (quote) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        // --- COMPANY LOGO & INFO ---
        const companyName = companySettings?.company_name || "L'Univers Diag";
        const companySub = "Garage & Reprogrammation Automobile";
        const companyPhone = companySettings?.phone || "07 58 04 43 77";
        const companyEmail = companySettings?.email || "contact@luniversdiag.fr";
        const companyAddress = companySettings?.address || "Toulouse";

        // Header
        doc.setFontSize(22);
        doc.setTextColor(37, 99, 235); // Blue primary
        doc.text(companyName, 20, 20);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(companySub, 20, 26);

        // Split address if too long or multiline
        const addressLines = doc.splitTextToSize(`${companyAddress} - ${companyPhone}`, 100);
        doc.text(addressLines, 20, 31);

        // Calculate Y position after address (in case it wrapped)
        let headerY = 31 + (addressLines.length * 4);
        doc.text(companyEmail, 20, headerY);

        // Title
        doc.setFontSize(16);
        doc.setTextColor(0);
        doc.text("Récapitulatif Devis", pageWidth - 20, 25, { align: 'right' });
        doc.setFontSize(10);
        doc.text(`Date: ${new Date(quote.created).toLocaleDateString()}`, pageWidth - 20, 31, { align: 'right' });
        doc.text(`Réf: ${quote.id.substring(0, 8).toUpperCase()}`, pageWidth - 20, 36, { align: 'right' });

        // Customer & Vehicle Info
        const separatorY = Math.max(headerY + 10, 42);
        doc.setDrawColor(200);
        doc.line(20, separatorY, pageWidth - 20, separatorY);

        const infoY = separatorY + 10;

        doc.setFontSize(11);
        doc.text("Client :", 20, infoY);
        doc.setFont("helvetica", "bold");
        doc.text(`${quote.firstName} ${quote.lastName}`, 20, infoY + 5);
        doc.setFont("helvetica", "normal");
        doc.text(quote.email, 20, infoY + 10);
        if (quote.phone) doc.text(quote.phone, 20, infoY + 15);

        doc.text("Véhicule :", pageWidth / 2, infoY);
        doc.setFont("helvetica", "bold");
        doc.text(`${quote.vehicle_brand} ${quote.vehicle_model}`, pageWidth / 2, infoY + 5);
        doc.setFont("helvetica", "normal");
        doc.text(`Immatriculation : ${quote.vehicle_plate}`, pageWidth / 2, infoY + 10);
        if (quote.vehicle_year) doc.text(`Année : ${quote.vehicle_year} - ${quote.vehicle_fuel}`, pageWidth / 2, infoY + 15);

        doc.line(20, infoY + 23, pageWidth - 20, infoY + 23);

        // Services Table
        const tableRows = [];

        if (quote.services_list) {
            quote.services_list.split(',').forEach(service => {
                tableRows.push([service.trim(), '-']);
            });
        } else {
            if (quote.service_key) tableRows.push(['Reproduction Clé', '-']);
            if (quote.service_ethanol) tableRows.push(['Conversion Éthanol', '-']);
            if (quote.service_diagnostic) tableRows.push(['Diagnostic Électronique', '-']);
            if (quote.service_urgent) tableRows.push(['Service Urgent', '-']);
        }

        tableRows.push(['', '']);

        autoTable(doc, {
            startY: infoY + 33,
            head: [['Prestation', 'Détail']],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [37, 99, 235] },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 40, halign: 'right' },
            },
            foot: [['TOTAL ESTIMÉ', `${quote.total_price} €`]],
            footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
        });

        // Footer & Legal
        const finalY = doc.lastAutoTable.finalY + 15;
        doc.setFontSize(9);
        doc.setTextColor(100);

        let currentY = finalY;
        doc.text("* Ce document est un récapitulatif de votre demande.", 20, currentY);
        currentY += 5;
        doc.text("* Le prix final sera confirmé lors du rendez-vous.", 20, currentY);

        currentY += 10;

        if (companySettings?.siret) {
            doc.setFontSize(8);
            doc.setTextColor(150);
            const legalText = `SIRET: ${companySettings.siret} ${companySettings.tva_number ? `| TVA: ${companySettings.tva_number}` : ''}`;
            doc.text(legalText, 20, currentY);
            currentY += 4;
        }

        if (companySettings?.rib_iban) {
            doc.setFontSize(8);
            doc.setTextColor(150);
            const ribText = `IBAN: ${companySettings.rib_iban} | BIC: ${companySettings.rib_bic || ''} | Banque: ${companySettings.rib_bank || ''}`;
            // Split if too long
            const ribLines = doc.splitTextToSize(ribText, pageWidth - 40);
            doc.text(ribLines, 20, currentY);
        }

        doc.save(`Devis_${quote.vehicle_brand}_${new Date(quote.created).toISOString().split('T')[0]}.pdf`);
    };

    if (loading) return <div className="account-content-wrapper">Chargement de vos devis...</div>;
    if (!user) return <div className="account-content-wrapper">Veuillez vous connecter.</div>;

    const getStatusBadge = (status) => {
        const labels = {
            en_attente: 'En attente',
            valide: 'Validé',
            refuse: 'Refusé',
            termine: 'Terminé'
        };
        return <span className={`status-badge ${status}`}>{labels[status] || status}</span>;
    };

    return (
        <div className="account-content-wrapper">
            <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Mes Devis & Demandes</h2>
            <p style={{ color: '#94a3b8', marginBottom: '2rem' }}>Retrouvez ici l'historique de vos demandes de devis.</p>

            <div className="card full-width-card">
                <div className="table-responsive">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Véhicule</th>
                                <th>Services</th>
                                <th>Date</th>
                                <th>Estimation</th>
                                <th>Statut</th>
                                <th className="text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quotes.length > 0 ? quotes.map((quote) => (
                                <tr key={quote.id}>
                                    <td>
                                        <strong>{quote.vehicle_brand} {quote.vehicle_model}</strong>
                                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{quote.vehicle_plate}</div>
                                    </td>
                                    <td>
                                        <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={quote.services_list}>
                                            {quote.services_list || 'Services divers'}
                                        </div>
                                    </td>
                                    <td>{new Date(quote.created).toLocaleDateString()}</td>
                                    <td><strong>{quote.total_price} €</strong></td>
                                    <td>{getStatusBadge(quote.status)}</td>
                                    <td className="text-right">
                                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                            <button
                                                onClick={() => setPreviewQuote(quote)}
                                                className="icon-btn"
                                                title="Voir le devis"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#facc15' }}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                    <circle cx="12" cy="12" r="3"></circle>
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => generatePDF(quote)}
                                                className="icon-btn"
                                                title="Télécharger PDF"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}
                                            >
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                    <polyline points="7 10 12 15 17 10"></polyline>
                                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                                        Aucune demande de devis trouvée.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Preview Modal */}
            {previewQuote && (
                <div className="modal-backdrop" onClick={() => setPreviewQuote(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="modal-header-flex">
                            <h3>Aperçu du Devis</h3>
                            <button className="modal-close" onClick={() => setPreviewQuote(null)}>×</button>
                        </div>

                        <div className="preview-container" style={{ color: '#f1f5f9' }}>
                            {/* Header Info */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem', borderBottom: '1px solid rgba(148, 163, 184, 0.1)', paddingBottom: '1rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, color: '#3b82f6' }}>{companySettings?.company_name || "L'Univers Diag"}</h4>
                                    <p style={{ margin: '0.2rem 0', fontSize: '0.85rem', color: '#94a3b8' }}>Garage & Reprogrammation</p>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8' }}>{companySettings?.phone || "07 58 04 43 77"}</p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>DEVIS ESTIMATIF</div>
                                    <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{new Date(previewQuote.created).toLocaleDateString()}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Réf: {previewQuote.id.substring(0, 8).toUpperCase()}</div>
                                </div>
                            </div>

                            {/* Info Blocks */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                                <div style={{ background: 'rgba(148, 163, 184, 0.05)', padding: '1rem', borderRadius: '0.5rem' }}>
                                    <h5 style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8' }}>Client</h5>
                                    <div style={{ fontWeight: 'bold' }}>{previewQuote.firstName} {previewQuote.lastName}</div>
                                    <div style={{ fontSize: '0.9rem' }}>{previewQuote.email}</div>
                                    <div style={{ fontSize: '0.9rem' }}>{previewQuote.phone}</div>
                                </div>
                                <div style={{ background: 'rgba(148, 163, 184, 0.05)', padding: '1rem', borderRadius: '0.5rem' }}>
                                    <h5 style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', textTransform: 'uppercase', color: '#94a3b8' }}>Véhicule</h5>
                                    <div style={{ fontWeight: 'bold' }}>{previewQuote.vehicle_brand} {previewQuote.vehicle_model}</div>
                                    <div style={{ fontSize: '0.9rem' }}>{previewQuote.vehicle_plate}</div>
                                    <div style={{ fontSize: '0.9rem' }}>{previewQuote.vehicle_year} - {previewQuote.vehicle_fuel}</div>
                                </div>
                            </div>

                            {/* Services List */}
                            <h5 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#f1f5f9' }}>Détail des prestations</h5>
                            <div style={{ border: '1px solid rgba(148, 163, 184, 0.1)', borderRadius: '0.5rem', overflow: 'hidden', marginBottom: '1.5rem' }}>
                                {previewQuote.services_list ? (
                                    previewQuote.services_list.split(',').map((service, idx) => (
                                        <div key={idx} style={{
                                            padding: '0.75rem 1rem',
                                            borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            fontSize: '0.9rem'
                                        }}>
                                            <span>{service.trim()}</span>
                                            <span style={{ color: '#94a3b8' }}>-</span>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '1rem', color: '#94a3b8', fontStyle: 'italic' }}>Détail non disponible</div>
                                )}
                                <div style={{
                                    padding: '1rem',
                                    background: 'rgba(30, 41, 59, 0.5)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderTop: '1px solid rgba(148, 163, 184, 0.1)'
                                }}>
                                    <span style={{ fontWeight: 'bold' }}>TOTAL ESTIMÉ</span>
                                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#facc15' }}>{previewQuote.total_price} €</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    onClick={() => generatePDF(previewQuote)}
                                    className="primary-button full-width"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                    Télécharger le PDF
                                </button>
                                <button
                                    onClick={() => setPreviewQuote(null)}
                                    className="secondary-button full-width"
                                >
                                    Fermer
                                </button>
                            </div>

                            {/* Company Footer Preview in Modal */}
                            {(companySettings?.siret || companySettings?.rib_iban) && (
                                <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(148, 163, 184, 0.03)', borderRadius: '0.5rem', fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
                                    {companySettings.siret && <div>SIRET: {companySettings.siret} {companySettings.tva_number ? `| TVA: ${companySettings.tva_number}` : ''}</div>}
                                    {companySettings.rib_iban && <div style={{ marginTop: '0.25rem' }}>Banque: {companySettings.rib_bank} | BIC: {companySettings.rib_bic}<br />IBAN: {companySettings.rib_iban}</div>}
                                </div>
                            )}

                            <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.75rem', color: '#64748b' }}>
                                * Prix indicatif, sous réserve de validation technique lors du RDV.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Quotes;
